import { z } from 'zod';
import {
  CALENDAR_COLOUR_MODES,
  GLOW_INTENSITIES,
  SURFACE_THEME_VAR_NAMES,
  SURFACE_TILE_IDS,
  THEME_CALENDAR_COLOURS_KEY,
  THEME_EFFECT_GLOW_KEY,
  THEME_SURFACE_TILE_KEY,
  THEME_VARS,
} from '../theme';

// Rebrand theme editor payload — the SINGLE shared shape for both write paths:
//   • PUT /v2/organizations/:id/rebrand-theme  (per-org override, full-replace)
//   • PUT /v2/tenants/rebrand-theme            (tenant default theme, merge)
// The allow-list of keys is derived from the shared theme catalog (single source
// of truth) — only the `editable` vars can be overridden (the two brand anchors:
// text colour is derived and never chosen), plus the non-colour settings
// (`theme/themeEffects.ts`, `theme/surfaceTiles.ts`). `.strict()` makes any
// other key (a non-editable catalog var, the text and action vars included, or
// an unknown one) a 422 unrecognized_keys error, and each colour must be a
// #RRGGBB hex. Every key is optional so the client may send any subset.
const RebrandHexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, { message: 'VALIDATION__InvalidHexColor' });

/**
 * The first stored theme value that is not a colour: how strongly the brand glow
 * burns for this layer. The background tile and the calendar colour mode, below,
 * ride in the record on the same terms.
 *
 * It rides in the same record because `Organizations.theme` is already
 * `Record<string, string>`, already full-replace, and already behind the
 * `org_rebrand_manage` permission and the `rebrandActive` billing gate — a
 * second column would duplicate all four and add a migration for one enum.
 *
 * It is safe there because `mergeOrgTheme` filters an org layer down to the
 * editable CATALOG VAR NAMES and strips the setting keys from the tenant layer
 * before merging, so this key structurally cannot reach `:root` as a CSS
 * variable. See `theme/themeEffects.ts`.
 *
 * Built as an ENTRY rather than spread in as an object literal: these shapes
 * are `Object.fromEntries` results, i.e. index-signature types, and spreading a
 * literal key over one of those DROPS the index signature — which silently
 * narrowed `ApiRebrandThemeDto` to `{ glow?: … }` and took every colour key out
 * of the DTO's static type while the runtime schema stayed correct.
 */
const glowShapeEntry = [
  THEME_EFFECT_GLOW_KEY,
  z.enum(GLOW_INTENSITIES).optional(),
] as const;

/**
 * The two settings only an org or a corp org holds (rebrand v2): the background
 * tile and the calendar colour mode. Same rules as glow — an ENTRY, so the
 * `Object.fromEntries` index signature survives — and no `null`: these routes
 * REPLACE the record, so omission already removes a key.
 *
 * `surface` takes ALL ten catalog ids, or a custom `#rrggbb` colour, stored
 * lowercase. Which tiles a NEW choice may land on (`ENABLED_SURFACE_TILE_IDS`)
 * is a service rule, not a schema rule: an already-stored tile that is not
 * enabled yet must still survive an unrelated save, and the DTO cannot tell
 * the two apart. A custom colour is not a tile and is never behind that gate.
 *
 * Neither is in `tenantEditableThemeShape`: the tenant has no tile (it keeps
 * its free surface pickers) and does not carry the calendar mode, so widening
 * this shape must not widen tenant storage.
 */
const surfaceTileShapeEntry = [
  THEME_SURFACE_TILE_KEY,
  z
    .union([
      z.enum(SURFACE_TILE_IDS),
      RebrandHexColorSchema.transform((color) => color.toLowerCase()),
    ])
    .optional(),
] as const;

const calendarColoursShapeEntry = [
  THEME_CALENDAR_COLOURS_KEY,
  z.enum(CALENDAR_COLOUR_MODES).optional(),
] as const;

const rebrandThemeShape = Object.fromEntries([
  ...THEME_VARS.filter((themeVar) => themeVar.editable).map(
    (themeVar) => [themeVar.varName, RebrandHexColorSchema.optional()] as const,
  ),
  glowShapeEntry,
  surfaceTileShapeEntry,
  calendarColoursShapeEntry,
]);

export const RebrandThemeDto = z.object(rebrandThemeShape).strict();

export type ApiRebrandThemeDto = z.infer<typeof RebrandThemeDto>;

/**
 * The semantic status colours — success / error / warning / event.
 *
 * They are `editable: false` in the catalog on purpose: a rebrand must not
 * repaint "this failed" or "this session was cancelled". But they ARE tenant
 * settings, and until now the tenant page's hand-rolled grid was the only place
 * to set them. Moving that page onto the shared rebrand editor would have
 * silently dropped the capability, so the tenant write path accepts them as a
 * separate, super-admin-only layer.
 *
 * Deliberately NOT the full "everything non-editable" set: `secondary-*`,
 * `action-selected`, `common-onBackgroundChannel` and `transparent` were never
 * on the page, and two of them hold `rgba(var(…))` strings rather than hex.
 */
const SEMANTIC_THEME_GROUPS = ['error', 'event', 'success', 'warning'];

export const SEMANTIC_THEME_VAR_NAMES: readonly string[] = THEME_VARS.filter(
  (themeVar) =>
    !themeVar.editable &&
    !themeVar.derived &&
    SEMANTIC_THEME_GROUPS.includes(themeVar.group),
).map((themeVar) => themeVar.varName);

const semanticThemeShape = Object.fromEntries(
  SEMANTIC_THEME_VAR_NAMES.map((varName) => [
    varName,
    RebrandHexColorSchema.optional(),
  ]),
);

// The five surface vars live in the theme catalog, next to their definitions,
// because `mergeOrgTheme` reads them. Re-exported so `@mfw/common/dtos` callers
// keep importing them from the DTOs.
export { SURFACE_THEME_VAR_NAMES };

/**
 * `.nullable()` on the TENANT layer only, and it is the only way a stored
 * value can be taken back out.
 *
 * The tenant route MERGES, so an omitted key survives, and every other value is
 * a hex — which meant a tenant that had ever saved a surface stayed pinned to
 * that ladder forever: no UI and no API could return it to the derived one, and
 * its own brand changes stopped moving its chrome.
 *
 * The brand inputs and the semantic vars get no such escape: each has a
 * meaningful value at every layer beneath it, so "remove mine" is not a state
 * they have. A surface is a var whose absence MEANS something — follow the
 * brand. (The glow, in `tenantEditableThemeShape`, is the other such key.)
 */
const surfaceThemeShape = Object.fromEntries(
  SURFACE_THEME_VAR_NAMES.map((varName) => [
    varName,
    RebrandHexColorSchema.nullable().optional(),
  ]),
);

/**
 * The editable vars as the TENANT route accepts them: the two brand anchors, hex
 * only — a brand input has a meaningful value at every layer beneath it, so
 * "remove mine" is not a state it has — plus the one setting the tenant carries.
 *
 * Until rebrand v2 this was also where the derived pickers (the text ladder,
 * `action-active`) were admitted as nullable, so the editor's "Follow surface"
 * action could take a stored value back out of a layer that MERGES. Text colour
 * is never chosen any more: those five vars are not editable, so they are in no
 * tenant shape at all — a hex and a `null` alike are an unrecognized key, and
 * `keepStorableTenantThemeVars` drops a stored copy from a legacy row.
 *
 * NOT the shared `rebrandThemeShape`: it carries the org-only settings, and the
 * org and corp routes REPLACE their stored theme, so a null they have no use for
 * must stay a 422.
 */
const tenantEditableThemeShape = Object.fromEntries([
  ...THEME_VARS.filter((themeVar) => themeVar.editable).map(
    (themeVar) => [themeVar.varName, RebrandHexColorSchema.optional()] as const,
  ),
  // The tenant may set the DEFAULT glow its organizations inherit; an org
  // overrides it (`resolveGlow`). It is the one setting the tenant carries, and
  // it is listed here — in the tenant's OWN shape — so that
  // `TENANT_STORABLE_THEME_VAR_NAMES`, and therefore
  // `keepStorableTenantThemeVars`, keeps it: both read exactly the keys
  // `TenantThemeDto` accepts, which is what its own doc promises.
  //
  // NULLABLE here and only here, for the same reason the surfaces are: this is
  // a key whose ABSENCE means something — "inherit the layer below", which is
  // not the same state as `full`. The tenant route MERGES, so an omitted key
  // survives; without an explicit null a tenant that ever saved a glow would be
  // pinned to it forever, with no UI and no API able to return its
  // organizations to the default. The org and corp routes REPLACE, so omission
  // already removes the key there and a null stays a 422.
  [THEME_EFFECT_GLOW_KEY, z.enum(GLOW_INTENSITIES).nullable().optional()],
]);

/**
 * What `PUT /v2/tenants/rebrand-theme` accepts: the editable vars (the two brand
 * anchors, see `tenantEditableThemeShape`) plus the semantic ones plus the
 * surface ones. `RebrandThemeDto` itself is NOT widened with them — it is
 * shared verbatim by the organization and corp routes, and widening it there
 * would let an org repaint its own status colours or hand-set surfaces that
 * should follow its brand hue. The widening runs the other way too: the
 * org-only settings `RebrandThemeDto` carries (`surface`, `calendarColours`)
 * are not in this shape, so the strict schema rejects them here.
 *
 * Derived vars (mainChannel, contrastText, dim-*) stay out of both: they are
 * computed from their source, and a stored copy is what let a stale
 * `mainChannel` survive a brand change. The surface vars are the one derived
 * exception, and only on the tenant layer — see `SURFACE_THEME_VAR_NAMES`.
 */
const tenantThemeShape = {
  ...tenantEditableThemeShape,
  ...semanticThemeShape,
  ...surfaceThemeShape,
};

export const TenantThemeDto = z.object(tenantThemeShape).strict();

export type ApiTenantThemeDto = z.infer<typeof TenantThemeDto>;

/**
 * Whether a layer holds SOME of the five surfaces but not all of them.
 *
 * The ladder is all-or-nothing, and until this predicate existed only
 * convention said so: every surface key above is independently `.optional()`,
 * the tenant route MERGES, and `POST /v2/tenants` takes a loose
 * `z.record(z.any(), z.any())`. No such row exists because the branding form
 * happens to submit all five — a property of one client, not of the column.
 *
 * A fragment has no reading. `text-primary` derives from `background-default`
 * ALONE, and the app takes `data-mfw-surface` and MUI's `palette.mode` from the
 * same var — so storing just that one puts black text on a still-dark `paper`
 * at 1.18:1 and flips the CSS-side ladder for the whole chrome while four
 * surfaces stay on the other one. Storing just `paper` is the mirror image:
 * white text on cream, 1.08:1, on every card in the app.
 *
 * Says nothing about whether five hand-picked colours READ well together —
 * that is the owner's palette and the editor's badge advises on it. This is
 * only about our derivation not being asked to straddle two ladders.
 */
export function hasPartialSurfaceLadder(theme: unknown): boolean {
  if (typeof theme !== 'object' || theme === null || Array.isArray(theme)) {
    return false;
  }

  const stored = SURFACE_THEME_VAR_NAMES.filter(
    (varName) => (theme as Record<string, unknown>)[varName] !== undefined,
  ).length;

  return stored > 0 && stored < SURFACE_THEME_VAR_NAMES.length;
}

/**
 * Every var a `TenantSettings.theme` layer may legitimately hold — the keys of
 * the very shape `TenantThemeDto` is built from, so the two cannot drift.
 *
 * Deliberately NOT built from `rebrandThemeShape`: that is the org/corp shape,
 * which carries the org-only settings (`surface`, `calendarColours`). Starting
 * from it would let every widening of the org shape widen what the tenant
 * column may store, and the tenant has neither a tile nor a calendar mode.
 */
export const TENANT_STORABLE_THEME_VAR_NAMES: readonly string[] =
  Object.keys(tenantThemeShape);

/**
 * The write-side twin of `TenantThemeDto`: drops every key that DTO would
 * reject.
 *
 * Dropping DERIVED vars was only half the rule, and the smaller half. The old
 * tenant page seeded its form from the saving admin's whole RESOLVED runtime
 * palette — including that admin's own ORG rebrand — and submitted all of it,
 * while `POST /v2/tenants` still accepts `theme: z.record(z.any(), z.any())`.
 * So rows carry catalog vars no route can edit (`secondary-*`,
 * `action-selected`, `common-onBackgroundChannel`) and keys that are not
 * catalog vars at all, each one spread over the catalog defaults on every
 * render for every user of that tenant.
 *
 * Nothing could take them out again: `PUT /rebrand-theme` MERGES and is
 * `.strict()` to the editable set, `theme` was removed from `PATCH :tenantId`,
 * and the column can never be reset to NULL. So the removal has to happen where
 * the value is written.
 *
 * A PARTIAL surface ladder is dropped for the same reason and by the same rule
 * — see `hasPartialSurfaceLadder`. This is the column's own guarantee, so no
 * route can leave a fragment behind, including `POST /v2/tenants`, whose
 * `theme` is a loose record no schema narrows. Dropping to the derived ladder
 * is what "Follow brand automatically" already does; keeping the fragment is
 * the one outcome with no reading. The rebrand route refuses a DELIBERATE
 * partial write before it reaches here, so a caller who meant it is told;
 * silence is reserved for a row that was already broken.
 */
export function keepStorableTenantThemeVars(
  theme: unknown,
): Record<string, string> {
  if (typeof theme !== 'object' || theme === null || Array.isArray(theme)) {
    return {};
  }

  const storable = new Set(TENANT_STORABLE_THEME_VAR_NAMES);
  const dropSurfaces = hasPartialSurfaceLadder(theme);

  return Object.fromEntries(
    Object.entries(theme as Record<string, string>).filter(
      ([varName]) =>
        storable.has(varName) &&
        !(dropSurfaces && SURFACE_THEME_VAR_NAMES.includes(varName)),
    ),
  );
}
